import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../../../components/Box";
import { Helpers } from "../../../../helpers/Helpers";
import { KTCard } from "../../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../../_metronic/layout/core";
import { storeActivityIndicator } from "../../../../redux/reducers/authSlice";
import { RootState } from "../../../../redux/store";
import CustomSingleSelect, { CustomSingleSelectModel } from "../../../../components/CustomSingleSelect";
import CustomNumberInput from "../../../../components/CustomNumberInput";
import CustomTextInput from "../../../../components/CustomTextInput";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import FileArchiveActions from "../../../../components/FileArchiveActions";
import { paraMetniniOku, paraYaz } from "../../../../components/paraGirdisi";
import { BELGE_TURU, DOSYA_UZANTILARI, DURUM_ADLARI, bitisOnerisi, bugunStr, dosyaHatasi, durum, durumRozeti, gecerliTarih, kalanGun, tarihGoster, uyari, uyariAdi, uyariRozeti, yenilemeTarihleri } from "../../../../helpers/sigortaHesap";
import { SIGORTA_SAYFASI, SigortaAracBilgisi, SigortaDosyasi, SigortaKaydetSonucu, SigortaKaydi, SigortaSecenekleri, SigortaService } from "../../../../services/SigortaService";

const SALT_OKUNUR: React.CSSProperties = { background: '#f1f1f4', border: '1px solid #F1F1F4', minHeight: 42 };

/**
 * Genel Giderler > Sigorta / Kasko > kayıt (28.10.2026, #502). Dört bölüm: Evrak Bilgileri, Araç Bilgileri,
 * Sigorta / Kasko Detayları, Belge Yönetimi.
 * - Araç Sigortası'nda Treyler (Dorse) / Yarı Römork seçilemez; belge türü değişince uygun olmayan araç temizlenir.
 * - Bitiş = başlangıç + 1 takvim yılı önerilir; bitiş elle değiştirildiyse başlangıç değişince ezilmez, öneri gösterilir.
 * - ?kopya=<id>: seçilen poliçeden yeni yılın poliçesi (poliçe no, tutar ve belgeler boş; yeni başlangıç = eski bitiş).
 * - En az bir poliçe belgesi (PDF / JPG / PNG, en fazla 10 MB) zorunlu. Tarih çakışması kaydı engellemez, uyarır.
 */
const SigortaAdd = () => {
    const navigate = useNavigate();
    const pageName = SIGORTA_SAYFASI
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth)
    const menu = auth.user.user?.data.authorizedMenus.find(x => x.menuName == pageName);
    const dosyaSecici = useRef<HTMLInputElement>(null);

    const [secenekler, setSecenekler] = useState<SigortaSecenekleri | undefined>(undefined);
    const [araclar, setAraclar] = useState<SigortaAracBilgisi[]>([]);
    const [kayitId, setKayitId] = useState<string | undefined>(undefined);
    const [kopyalanan, setKopyalanan] = useState<SigortaKaydi | undefined>(undefined);
    const [kayit, setKayit] = useState<SigortaKaydi | undefined>(undefined);

    const [belgeTuru, setBelgeTuru] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [arac, setArac] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [aracBilgi, setAracBilgi] = useState<{ aracCinsi?: string, markaModel?: string, modelYili?: number } | undefined>(undefined);
    const [policeNo, setPoliceNo] = useState<string | undefined>(undefined);
    const [baslangic, setBaslangic] = useState<string | undefined>(undefined);
    const [bitis, setBitis] = useState<string | undefined>(undefined);
    const [bitisElle, setBitisElle] = useState<boolean>(false);
    const [sigortaFirmasi, setSigortaFirmasi] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [acente, setAcente] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [tutar, setTutar] = useState<string | undefined>(undefined);
    const [doviz, setDoviz] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [teminat, setTeminat] = useState<string | undefined>(undefined);
    const [ekTeminat, setEkTeminat] = useState<string | undefined>(paraYaz(0));
    const [aciklama, setAciklama] = useState<string>('');
    const [mevcutDosyalar, setMevcutDosyalar] = useState<SigortaDosyasi[]>([]);
    const [yeniDosyalar, setYeniDosyalar] = useState<File[]>([]);
    const [cakismaUyarisi, setCakismaUyarisi] = useState<string | undefined>(undefined);

    const saltOkunur = kayitId ? !menu?.canUpdate : !menu?.canInsert;
    const tur = belgeTuru ? Number(belgeTuru.value) : undefined;
    const bugun = bugunStr();
    const tarihlerTamam = gecerliTarih(baslangic) && gecerliTarih(bitis) && bitis! > baslangic!;
    const oneri = gecerliTarih(baslangic) ? bitisOnerisi(baslangic!) : undefined;

    useEffect(() => {
        baslat(searchParams.get('id'), searchParams.get('kopya'));
    }, [])

    const aracSec = (a: { vehicleId: string, plaka?: string, aracCinsi?: string, markaModel?: string, modelYili?: number } | undefined) => {
        setArac(a ? { label: a.plaka ?? '', value: a.vehicleId } : undefined);
        setAracBilgi(a ? { aracCinsi: a.aracCinsi, markaModel: a.markaModel, modelYili: a.modelYili } : undefined);
    }

    const baslat = async (id: string | null, kopya: string | null) => {
        try {
            dispatch(storeActivityIndicator(true));

            const [s, p] = await Promise.all([SigortaService.secenekler(), SigortaService.plakalar()]);

            if (!s.isSuccess || !p.isSuccess) {
                toast.error((s.isSuccess ? p.message : s.message) ?? '');
                return;
            }

            const sec = s.data as SigortaSecenekleri;
            setSecenekler(sec);
            setAraclar(p.data as SigortaAracBilgisi[]);

            const kaynakId = id ?? kopya;
            if (!kaynakId) {
                const tl = sec.dovizCinsleri.find(x => x.value == sec.varsayilanDovizId);
                if (tl)
                    setDoviz({ label: tl.label, value: tl.value });
                return;
            }

            var response = await SigortaService.getById(kaynakId);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            const k = response.data as SigortaKaydi;
            setBelgeTuru({ label: k.belgeTuruAdi, value: String(k.belgeTuru) });
            aracSec(k);
            setSigortaFirmasi({ label: k.sigortaFirmasi ?? '', value: k.sigortaFirmasiId });
            setAcente({ label: k.acente ?? '', value: k.acenteId });
            setDoviz({ label: k.dovizCinsi ?? '', value: k.currencyTypeId });
            setTeminat(paraYaz(k.teminatBedeli));
            setEkTeminat(paraYaz(k.ekTeminatBedeli));
            setAciklama(k.aciklama ?? '');

            if (id) {
                setKayitId(k.sigortaPolicesiId);
                setKayit(k);
                setPoliceNo(k.policeNo);
                setBaslangic(k.baslangicTarihiDeger);
                setBitis(k.bitisTarihiDeger);
                setBitisElle(k.bitisTarihiDeger != bitisOnerisi(k.baslangicTarihiDeger));
                setTutar(paraYaz(k.tutar));
                setMevcutDosyalar(k.dosyalar ?? []);
            } else {
                // Kopyalama: poliçe no, tutar ve belgeler boş; tarihler yenileme kuralıyla.
                const y = yenilemeTarihleri(k.bitisTarihiDeger);
                setKopyalanan(k);
                setBaslangic(y.baslangic);
                setBitis(y.bitis);
                setBitisElle(false);
            }
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const liste = (al: (s: SigortaSecenekleri) => { value: number | string, label: string }[]) =>
        () => Promise.resolve({ isSuccess: true, data: secenekler ? al(secenekler).map(x => ({ label: x.label, value: String(x.value) })) : [] });

    const uygunAraclar = () => araclar.filter(x => tur != BELGE_TURU.TRAFIK || !x.treylerMi);

    const belgeTuruDegisti = (v: CustomSingleSelectModel | undefined) => {
        setBelgeTuru(v);
        const secili = araclar.find(x => x.vehicleId == arac?.value);
        if (v && Number(v.value) == BELGE_TURU.TRAFIK && secili?.treylerMi) {
            aracSec(undefined);
            toast(`${secili.plaka} (${secili.aracCinsi}) Araç Sigortası'nda seçilemez; araç seçimi temizlendi.`, { icon: 'ℹ️' });
        }
    }

    const baslangicDegisti = (v: string | undefined) => {
        setBaslangic(v);
        if (!bitisElle && gecerliTarih(v))
            setBitis(bitisOnerisi(v!));
    }

    const bitisDegisti = (v: string | undefined) => {
        setBitis(v);
        setBitisElle(true);
    }

    const dosyaEkle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const secilen = Array.from(e.target.files ?? []);
        e.target.value = '';
        const hatalar = secilen.map(d => dosyaHatasi(d.name, d.size)).filter(x => x);
        if (hatalar.length > 0)
            toast.error(hatalar.join('\n'));
        setYeniDosyalar([...yeniDosyalar, ...secilen.filter(d => !dosyaHatasi(d.name, d.size))]);
    }

    const kaydet = async () => {
        const eksik: string[] = [];
        if (!belgeTuru) eksik.push('Belge Türü');
        if (!arac) eksik.push('Araç Plaka');
        if (Helpers.isEmpty(policeNo?.trim())) eksik.push('Poliçe No');
        if (!gecerliTarih(baslangic)) eksik.push('Başlangıç Tarihi');
        if (!gecerliTarih(bitis)) eksik.push('Bitiş Tarihi');
        if (!sigortaFirmasi) eksik.push('Sigorta Firması');
        if (!acente) eksik.push('Acente');
        if (paraMetniniOku(tutar) === undefined) eksik.push('Tutar');
        if (!doviz) eksik.push('Döviz Cinsi');
        if (paraMetniniOku(teminat) === undefined) eksik.push('Teminat Bedeli');
        if (paraMetniniOku(ekTeminat) === undefined) eksik.push('Ek Teminat Bedeli');

        if (eksik.length > 0) {
            toast.error('Zorunlu alanları doldurun: ' + eksik.join(', '));
            return;
        }

        if (bitis! <= baslangic!) {
            toast.error('Bitiş tarihi başlangıç tarihinden sonra olmalı.');
            return;
        }

        if (mevcutDosyalar.length + yeniDosyalar.length == 0) {
            toast.error('En az bir poliçe belgesi yükleyin.');
            return;
        }

        try {
            dispatch(storeActivityIndicator(true));
            setCakismaUyarisi(undefined);

            var response = await SigortaService.kaydet({
                id: kayitId,
                belgeTuru: tur,
                vehicleId: arac?.value,
                policeNo: policeNo?.trim(),
                baslangicTarihi: baslangic,
                bitisTarihi: bitis,
                sigortaFirmasiId: sigortaFirmasi?.value,
                acenteId: acente?.value,
                tutar: paraMetniniOku(tutar),
                currencyTypeId: doviz?.value,
                teminatBedeli: paraMetniniOku(teminat),
                ekTeminatBedeli: paraMetniniOku(ekTeminat),
                aciklama: aciklama,
                kopyalananPoliceId: kayitId ? undefined : kopyalanan?.sigortaPolicesiId,
                kalanDosyaIdleri: mevcutDosyalar.map(x => x.id)
            }, yeniDosyalar);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            const sonuc = response.data as SigortaKaydetSonucu;
            const k = sonuc.kayit;
            toast.success('Poliçe kaydedildi.');
            if (sonuc.cakismaUyarisi) {
                setCakismaUyarisi(sonuc.cakismaUyarisi);
                toast(sonuc.cakismaUyarisi, { icon: '⚠️', duration: 8000 });
            }
            setKayitId(k.sigortaPolicesiId);
            setKayit(k);
            setPoliceNo(k.policeNo);
            setTutar(paraYaz(k.tutar));
            setMevcutDosyalar(k.dosyalar ?? []);
            setYeniDosyalar([]);

            if (!kayitId)
                navigate('/sigorta/listadd?id=' + k.sigortaPolicesiId, { replace: true });
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const durumKutusu = () => {
        if (!tarihlerTamam)
            return <div className="form-control form-control-solid text-muted" style={SALT_OKUNUR}>Tarihler girilince hesaplanır</div>;
        const d = durum(baslangic!, bitis!, bugun);
        return <div className="form-control form-control-solid" style={SALT_OKUNUR}><span className={'badge ' + durumRozeti(d)}>{DURUM_ADLARI[d]}</span></div>;
    }

    const kalanGunKutusu = () => {
        if (!tarihlerTamam)
            return <div className="form-control form-control-solid text-muted" style={SALT_OKUNUR}>—</div>;
        const kalan = kalanGun(bitis!, bugun);
        const u = uyari(baslangic!, bitis!, bugun);
        return (
            <div className="form-control form-control-solid d-flex justify-content-between align-items-center" style={SALT_OKUNUR}>
                <span className="fw-bold">{kalan}</span>
                <span className={'badge ' + uyariRozeti(u)}>{uyariAdi(u, kalan)}</span>
            </div>
        );
    }

    const saltKutu = (deger: string | number | undefined) => (
        <div className="form-control form-control-solid" style={SALT_OKUNUR}>{deger ?? ''}</div>
    );

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    {
                        kopyalanan && !kayitId && (
                            <div className="alert alert-primary d-flex align-items-center mb-5">
                                <i className="fa fa-copy fs-3 me-3 text-primary" />
                                <div>
                                    <b>{kopyalanan.policeNo}</b> numaralı poliçeden ({kopyalanan.baslangicTarihiStr} – {kopyalanan.bitisTarihiStr}) yeni poliçe oluşturuyorsunuz.
                                    Eski poliçe değişmez; yeni poliçe no, tutar ve poliçe belgesini girin.
                                </div>
                            </div>
                        )
                    }
                    {
                        cakismaUyarisi && (
                            <div className="alert alert-warning mb-5">{cakismaUyarisi}</div>
                        )
                    }

                    <KTCard className="ebbe-card">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Evrak Bilgileri</h4>

                            <div className="row">
                                <div className="col-md-4">
                                    <CustomSingleSelect
                                        labelText="Belge Türü"
                                        isLabelRequired
                                        disabled={saltOkunur}
                                        selectedValue={belgeTuru}
                                        onChange={belgeTuruDegisti}
                                        onMenuOpen={liste(s => s.belgeTurleri)}
                                        labelName='label'
                                        valueName='value'
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Durum</label>
                                    {durumKutusu()}
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Kalan Gün</label>
                                    {kalanGunKutusu()}
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Araç Bilgileri</h4>

                            <div className="row">
                                <div className="col-md-3">
                                    <CustomSingleSelect
                                        labelText="Plaka"
                                        isLabelRequired
                                        disabled={saltOkunur}
                                        selectedValue={arac}
                                        onChange={(v) => aracSec(v ? araclar.find(x => x.vehicleId == v.value) : undefined)}
                                        onMenuOpen={() => Promise.resolve({ isSuccess: true, data: uygunAraclar().map(x => ({ label: x.plaka, value: x.vehicleId })) })}
                                        labelName='label'
                                        valueName='value'
                                        isOrdering
                                        placeholder={tur == BELGE_TURU.TRAFIK ? 'Dorse / yarı römork hariç' : 'Aktif araçlar'}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">Araç Cinsi</label>
                                    {saltKutu(aracBilgi?.aracCinsi)}
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Marka / Model</label>
                                    {saltKutu(aracBilgi?.markaModel)}
                                </div>

                                <div className="col-md-2">
                                    <label className="form-label">Model Yılı</label>
                                    {saltKutu(aracBilgi?.modelYili)}
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Sigorta / Kasko Detayları</h4>

                            <div className="row">
                                <div className="col-md-4">
                                    <CustomTextInput labelText="Poliçe No" isLabelRequired isDisabled={saltOkunur} value={policeNo} setValue={setPoliceNo} placeholder={kopyalanan && !kayitId ? 'Yeni poliçenin numarası' : undefined} />
                                </div>

                                <div className="col-md-3">
                                    <CustomDatePicker labelText="Başlangıç Tarihi" isLabelRequired isDisabled={saltOkunur} value={baslangic ?? ''} setValue={baslangicDegisti} />
                                </div>

                                <div className="col-md-3">
                                    <CustomDatePicker labelText="Bitiş Tarihi" isLabelRequired isDisabled={saltOkunur} value={bitis ?? ''} setValue={bitisDegisti} />
                                    {
                                        !saltOkunur && bitisElle && oneri && oneri != bitis && (
                                            <div className="fs-8 mt-1">
                                                <span className="text-muted">Önerilen bitiş {tarihGoster(oneri)} </span>
                                                <a className="cursor-pointer fw-bold" onClick={() => { setBitis(oneri); setBitisElle(false); }}>Uygula</a>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                            <div className="text-muted fs-7 mt-2">Tarihleri poliçede yazdığı gibi girin (ör. 01.01.2026 – 01.01.2027). Bitiş, başlangıca bir yıl eklenerek önerilir; değiştirilebilir.</div>

                            <div className="row mt-5">
                                <div className="col-md-4">
                                    <CustomSingleSelect
                                        labelText="Sigorta Firması"
                                        isLabelRequired
                                        disabled={saltOkunur}
                                        selectedValue={sigortaFirmasi}
                                        onChange={setSigortaFirmasi}
                                        onMenuOpen={liste(s => s.sigortaFirmalari)}
                                        labelName='label'
                                        valueName='value'
                                        isOrdering
                                        placeholder="Firma Tanımları › Sigorta Firması"
                                    />
                                </div>

                                <div className="col-md-4">
                                    <CustomSingleSelect
                                        labelText="Acente"
                                        isLabelRequired
                                        disabled={saltOkunur}
                                        selectedValue={acente}
                                        onChange={setAcente}
                                        onMenuOpen={liste(s => s.acenteler)}
                                        labelName='label'
                                        valueName='value'
                                        isOrdering
                                        placeholder="Firma Tanımları › Acente"
                                    />
                                </div>
                            </div>

                            <div className="row mt-5">
                                <div className="col-md-3">
                                    <CustomNumberInput labelText="Tutar" isLabelRequired isDisabled={saltOkunur} value={tutar ?? ''} setValue={setTutar} paraBicimi />
                                </div>

                                <div className="col-md-2">
                                    <CustomSingleSelect
                                        labelText="Döviz Cinsi"
                                        isLabelRequired
                                        disabled={saltOkunur}
                                        selectedValue={doviz}
                                        onChange={setDoviz}
                                        onMenuOpen={liste(s => s.dovizCinsleri)}
                                        labelName='label'
                                        valueName='value'
                                    />
                                </div>

                                <div className="col-md-3">
                                    <CustomNumberInput labelText="Teminat Bedeli" isLabelRequired isDisabled={saltOkunur} value={teminat ?? ''} setValue={setTeminat} paraBicimi />
                                </div>

                                <div className="col-md-3">
                                    <CustomNumberInput labelText="Ek Teminat Bedeli" isLabelRequired isDisabled={saltOkunur} value={ekTeminat ?? ''} setValue={setEkTeminat} paraBicimi />
                                </div>
                            </div>
                            <div className="text-muted fs-7 mt-2">
                                Tutar poliçe için ödenen toplam primdir; araç maliyetine yalnız tutar girer, teminat bedelleri gider değildir.
                                {kayit && kayit.kur != 1 ? ` TL karşılığı ${paraYaz(kayit.tutarTL)} TL (başlangıç tarihi kuru ${kayit.kur.toLocaleString('tr-TR', { maximumFractionDigits: 6 })}).` : ''}
                            </div>

                            <div className="row mt-5">
                                <div className="col-md-12">
                                    <label className="form-label">Açıklama</label>
                                    <textarea
                                        id="sigorta-aciklama"
                                        disabled={saltOkunur}
                                        rows={3}
                                        value={aciklama}
                                        onChange={(e) => setAciklama(e.target.value)}
                                        style={{ background: (saltOkunur ? '#f1f1f4' : undefined), border: '1px solid #F1F1F4' }}
                                        className="form-control form-control-solid"
                                    />
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-3 text-black required">Belge Yönetimi</h4>
                            <div className="text-muted fs-7 mb-5">
                                En az bir poliçe belgesi zorunlu. {DOSYA_UZANTILARI.map(x => x.replace('.', '').toUpperCase()).filter((x, i, a) => a.indexOf(x) == i).join(', ')}; dosya başına en fazla {secenekler?.azamiDosyaMb ?? 10} MB.
                                {kopyalanan && !kayitId ? ' Kopyalanan poliçenin belgeleri yeni poliçeye taşınmaz.' : ''}
                            </div>

                            <table className="table align-middle table-row-dashed ebbe-compact-table mb-3">
                                <tbody className="text-gray-700 fw-bold">
                                    {
                                        mevcutDosyalar.length + yeniDosyalar.length == 0 && (
                                            <tr><td className="text-muted">Henüz belge eklenmedi.</td></tr>
                                        )
                                    }
                                    {
                                        mevcutDosyalar.map(d => (
                                            <tr key={d.id}>
                                                <td><i className="fa fa-file-alt me-2 text-primary" />{d.ad}</td>
                                                <td style={{ width: 1 }}><FileArchiveActions url={d.url} fileName={d.ad} /></td>
                                                <td style={{ width: 1 }}>
                                                    {
                                                        !saltOkunur && (
                                                            <button className="btn btn-sm btn-icon btn-light-danger" title="Belgeyi kaldır (kaydedince silinir)" onClick={() => setMevcutDosyalar(mevcutDosyalar.filter(x => x.id != d.id))}>
                                                                <i className="fa fa-trash" />
                                                            </button>
                                                        )
                                                    }
                                                </td>
                                            </tr>
                                        ))
                                    }
                                    {
                                        yeniDosyalar.map((d, i) => (
                                            <tr key={'yeni-' + i}>
                                                <td><i className="fa fa-file-upload me-2 text-success" />{d.name} <span className="badge badge-light-success ms-2">Kaydedince yüklenecek</span></td>
                                                <td style={{ width: 1 }} className="text-muted fs-8 text-nowrap">{(d.size / 1024 / 1024).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} MB</td>
                                                <td style={{ width: 1 }}>
                                                    <button className="btn btn-sm btn-icon btn-light-danger" title="Kaldır" onClick={() => setYeniDosyalar(yeniDosyalar.filter((_, j) => j != i))}>
                                                        <i className="fa fa-times" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>

                            {
                                !saltOkunur && (
                                    <React.Fragment>
                                        <input id="sigorta-dosya" ref={dosyaSecici} type="file" multiple accept={DOSYA_UZANTILARI.join(',')} onChange={dosyaEkle} hidden />
                                        <button className="btn btn-sm btn-light-primary" onClick={() => dosyaSecici.current?.click()}>
                                            <i className="fa fa-paperclip me-1" />
                                            Belge Ekle
                                        </button>
                                    </React.Fragment>
                                )
                            }
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <Box flexDirection="row" justifyContent="flex-end">
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                                </Box>
                                {
                                    !saltOkunur && (
                                        <Box>
                                            <button className="btn btn-primary btn-sm" onClick={kaydet}>Kaydet</button>
                                        </Box>
                                    )
                                }
                            </Box>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>
        </React.Fragment>
    )
}

export default SigortaAdd;
